// src/pages/Admin/AideConseils/AideTutoEnseignants.jsx
import React, { useEffect, useRef } from "react";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import Button from "../../../components/Button/Button";
import { QRCodeCanvas } from "qrcode.react";
import { FaExternalLinkAlt, FaDownload } from "react-icons/fa";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

export default function AideTutoEnseignants() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const docUrl = process.env.REACT_APP_ASSO_TUTO_ENSEIGNANT_URL || "";
  const siteUrl = process.env.REACT_APP_URL || "";

  const qrRef = useRef(null);

  useEffect(() => {
    document.title = "Aide & Tutos - Enseignants";
  }, []);

  const handleOpenDoc = () => {
    if (!docUrl) return;
    const url = docUrl.includes("/edit") ? docUrl.replace("/edit", "/preview") : docUrl;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadQR = () => {
    const canvasEl =
      qrRef.current instanceof HTMLCanvasElement
        ? qrRef.current
        : qrRef.current?.querySelector?.("canvas");
    if (!canvasEl) return;

    const scale = 2;
    const newCanvas = document.createElement("canvas");
    newCanvas.width = canvasEl.width * scale;
    newCanvas.height = canvasEl.height * scale;

    const ctx = newCanvas.getContext("2d");
    ctx.scale(scale, scale);
    ctx.drawImage(canvasEl, 0, 0);

    const pngUrl = newCanvas.toDataURL("image/png").replace("image/png", "image/octet-stream");

    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = "qr_site_club.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Enseignant"
          title="Aide & Tutos"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé + infos + action */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Un document d’aide & de tutoriels pour les enseignants
              </h2>
              <div className="mt-3 text-sm/6 max-w-3xl mx-auto text-center">
                <p>
                  Vous y trouverez des <span className="font-semibold">réponses aux questions fréquentes</span>, des{" "}
                  <span className="font-semibold">tutos pas à pas</span> et des{" "}
                  <span className="font-semibold">conseils d’utilisation</span>.
                </p>
                <p className="mt-1 opacity-90">
                  Le document peut évoluer : pensez à le consulter régulièrement.
                </p>
              </div>

              <div className="mt-5 flex items-center justify-center">
                <Button
                  text="Ouvrir le document d’aide"
                  icon={FaExternalLinkAlt}
                  onClick={handleOpenDoc}
                  className="!rounded-xl !shadow-md gap-2"
                />
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="px-5 pb-6 sm:px-8">
            {/* QR code vers le site public (si dispo) */}
            {siteUrl && (
              <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-5">
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  QR Code vers le site public
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Affichez-le dans vos dojos pour faciliter l’accès au site.
                </p>

                <div className="mt-4 flex flex-col sm:flex-row items-center gap-4">
                  <div className="bg-white rounded-xl shadow p-4">
                    <QRCodeCanvas
                      ref={qrRef}
                      value={siteUrl}
                      size={300}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="H"
                      includeMargin
                    />
                  </div>

                  <div className="flex-1 w-full">
                    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/40 p-4">
                      <p className="text-sm text-slate-700 dark:text-slate-300">
                        URL cible :
                      </p>
                      <p className="mt-1 break-all text-sm font-medium text-slate-900 dark:text-slate-100">
                        {siteUrl}
                      </p>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          text="Télécharger le QR Code"
                          icon={FaDownload}
                          onClick={handleDownloadQR}
                          className="!rounded-xl"
                        />
                        <a
                          href={siteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center rounded-xl px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                        >
                          Ouvrir le site ↗
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Aperçu du document (si dispo) */}
            <div className="mt-6">
              {docUrl ? (
                <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <iframe
                    title="Document d'aide enseignants"
                    src={docUrl.includes("/edit") ? docUrl.replace("/edit", "/preview") : docUrl}
                    className="w-full h-[75vh]"
                  />
                </div>
              ) : (
                <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-4 text-sm dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-800">
                  Aucun document d’aide n’est configuré pour le moment.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
